import { isUndefined } from 'lodash';
import { FC, useState, useRef, useCallback, cloneElement, isValidElement, useEffect } from 'react';
import { createPortal } from 'react-dom';
import cn from 'bem-cn';

import { useClickOutside } from '../../../hooks/useClickOutside';
import { useHover } from '../../../hooks/useHover';

import { usePopoverPosition, useAutoClose } from './Popover.hooks';
import { PopoverProps, ChildWithClick } from './Popover.types';
import { PopoverTail } from './PopoverTail';
import './Popover.scss';

const b = cn('popover');

export const Popover: FC<PopoverProps> = ({
    content,
    children,
    position = 'bottom',
    className,
    contentClassName,
    disabled = false,
    closeOnClickOutside = true,
    hasTail = true,
    autoClose = true,
    showOnHover = false,
    open: externalIsOpen,
    afterClose,
}) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const isControlled = !isUndefined(externalIsOpen);
    const isOpen = isControlled ? externalIsOpen : internalIsOpen;

    const triggerRef = useRef<HTMLDivElement>(null);
    const popoverRef = useRef<HTMLDivElement>(null);

    const { isHovered, onMouseEnter, onMouseLeave } = useHover();

    const coordinates = usePopoverPosition(triggerRef, popoverRef, isOpen, position);

    const handleToggle = useCallback(() => {
        if (disabled || showOnHover || isControlled) {
            return;
        }
        setInternalIsOpen(prev => !prev);
    }, [disabled, showOnHover, isControlled]);

    const handleClose = useCallback(() => {
        if (!isControlled) {
            setInternalIsOpen(false);
        }

        afterClose?.();
    }, [isControlled, afterClose]);

    useClickOutside({
        ref: popoverRef,
        isActive: closeOnClickOutside && isOpen,
        onClickOutside: handleClose,
    });

    useAutoClose(isOpen, handleClose, 2000, autoClose);

    const handleTriggerClick = useCallback(
        (e: React.MouseEvent) => {
            handleToggle();
            if (isValidElement(children)) {
                const originalOnClick = (children as ChildWithClick).props.onClick;
                originalOnClick?.(e);
            }
        },
        [children, handleToggle],
    );

    useEffect(() => {
        if (showOnHover && !isControlled) {
            setInternalIsOpen(isHovered);
        }
    }, [isHovered, showOnHover, isControlled]);

    useEffect(() => {
        if (!isUndefined(externalIsOpen)) {
            setInternalIsOpen(externalIsOpen);
        }
    }, [externalIsOpen]);

    const trigger = isValidElement(children)
        ? cloneElement(children as ChildWithClick, {
            onClick: handleTriggerClick,
        })
        : children;

    const popoverContent = isOpen && content && (
        <div
            key={String(content)}
            ref={popoverRef}
            className={b('content', { position, 'with-tail': hasTail }).mix(contentClassName)}
            style={{
                position: 'fixed',
                top: `${coordinates.top}px`,
                left: `${coordinates.left}px`,
            }}
            role="dialog"
            aria-modal="false"
        >
            {hasTail && (
                <div className={b('tail', { position })}>
                    <PopoverTail />
                </div>
            )}
            {content}
        </div>
    );

    return (
        <>
            <div
                ref={triggerRef}
                className={b('trigger-wrapper').mix(className)}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {trigger}
            </div>
            {popoverContent && createPortal(popoverContent, document.body)}
        </>
    );
};
